import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import bgImageLocal from "../../assets/pictures/15.jpg";
import { useAuth } from "../../context/auth-context";

type DepartamentoAcessivel = {
  id?: number | string | null;
  nome: string;
  slug?: string | null;
  routeKey?: string | null;
  themeColor?: string | null;
  iconKey?: string | null;
  modulos?: any[];
};

const removeAccents = (s: string) =>
  s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const norm = (s: string) => removeAccents(String(s)).toLowerCase();

const slugify = (s: string) =>
  norm(s)
    .replace(/&/g, "e")
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");

const getDeptRouteKey = (dept: DepartamentoAcessivel) => {
  if (dept.routeKey && String(dept.routeKey).trim()) {
    return String(dept.routeKey).trim().replace(/^\/+/, "");
  }

  if (dept.slug && String(dept.slug).trim()) {
    return String(dept.slug).trim().replace(/^\/+/, "");
  }

  return slugify(dept.nome || "departamento");
};

const getDeptDisplayColor = (
  dept: DepartamentoAcessivel,
  index: number
): string => {
  if (dept.themeColor && String(dept.themeColor).trim()) {
    return String(dept.themeColor);
  }

  const fallbackPalette = [
    "#1565c0",
    "#f39c12",
    "#13c2c2",
    "#ff5a47",
    "#7ac800",
    "#14a9d8",
    "#7b61ff",
    "#00a86b",
  ];

  return fallbackPalette[index % fallbackPalette.length];
};

const isStatusServicoDepartment = (dept: DepartamentoAcessivel) => {
  const nome = norm(dept.nome || "");
  const slug = norm(dept.slug || "");
  const routeKey = norm(dept.routeKey || "");

  return (
    nome === "status do servico" ||
    slug === "status-servico" ||
    routeKey === "status-do-servico"
  );
};

const DeptInternetIcon: React.FC<{
  iconKey?: string | null;
  size?: number;
  color?: string;
  alt?: string;
  style?: React.CSSProperties;
}> = ({ iconKey, size = 20, color = "#ffffff", alt = "", style }) => {
  const safeKey =
    typeof iconKey === "string" && iconKey.includes(":")
      ? iconKey
      : "lucide:circle";

  const src = `https://api.iconify.design/${safeKey}.svg?color=${encodeURIComponent(
    color
  )}`;

  return (
    <img
      src={src}
      alt={alt}
      width={size}
      height={size}
      draggable={false}
      loading="eager"
      style={{
        width: size,
        height: size,
        display: "block",
        flexShrink: 0,
        userSelect: "none",
        pointerEvents: "none",
        ...style,
      }}
      onError={(e) => {
        e.currentTarget.src =
          "https://api.iconify.design/lucide:circle.svg?color=%23ffffff";
      }}
    />
  );
};

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { sessao } = useAuth();

  const [developmentModule, setDevelopmentModule] =
    useState<DepartamentoAcessivel | null>(null);

  const bgImage = bgImageLocal;

  const displayName = useMemo(() => {
    const clean = String(sessao?.nome || "").trim();
    return clean || "Usuário";
  }, [sessao?.nome]);

  const departments = useMemo<DepartamentoAcessivel[]>(() => {
    const accessibleDepartments = sessao?.departamentos_acessiveis;

    const raw: any[] = Array.isArray(accessibleDepartments)
      ? accessibleDepartments
      : [];

    return raw
      .map((dept: any) => ({
        id: dept?.id ?? null,
        nome: String(dept?.nome || ""),
        slug:
          dept?.slug !== undefined && dept?.slug !== null
            ? String(dept.slug)
            : null,
        routeKey:
          dept?.routeKey !== undefined && dept?.routeKey !== null
            ? String(dept.routeKey)
            : dept?.route_key !== undefined && dept?.route_key !== null
            ? String(dept.route_key)
            : null,
        themeColor:
          dept?.themeColor !== undefined && dept?.themeColor !== null
            ? String(dept.themeColor)
            : dept?.theme_color !== undefined && dept?.theme_color !== null
            ? String(dept.theme_color)
            : null,
        iconKey:
          dept?.iconKey !== undefined && dept?.iconKey !== null
            ? String(dept.iconKey)
            : dept?.icon_key !== undefined && dept?.icon_key !== null
            ? String(dept.icon_key)
            : null,
        modulos: Array.isArray(dept?.modulos) ? dept.modulos : [],
      }))
      .filter((dept) => dept.nome.trim().length > 0);
  }, [sessao?.departamentos_acessiveis]);

  const handleDepartmentClick = (dept: DepartamentoAcessivel) => {
    if (isStatusServicoDepartment(dept)) {
      setDevelopmentModule(dept);
      return;
    }

    const routeKey = getDeptRouteKey(dept);
    navigate(`/${routeKey}`);
  };

  const closeDevelopmentMessage = () => {
    setDevelopmentModule(null);
  };

  return (
    <div style={styles.page}>
      <div
        style={{
          ...styles.background,
          backgroundImage: `url(${bgImage})`,
        }}
      />
      <div style={styles.edgeBlur} />
      <div style={styles.topTint} />
      <div style={styles.overlay} />

      <div style={styles.content}>
        <header style={styles.customHeader}>
          <div style={styles.customHeaderLeft}>
            <div />
          </div>
        </header>

        <section style={styles.leftTopArea}>
          <div style={styles.headerBlock}>
            <h1 style={styles.title}>
              <span style={styles.titleHello}>Olá,</span>{" "}
              <span style={styles.titleName}>{displayName}!</span>
            </h1>

            <p style={styles.subtitle}>
              Em qual departamento vamos trabalhar hoje?
            </p>
          </div>

          <div style={styles.modulesRow}>
            {departments.map((dept, index) => {
              const deptColor = getDeptDisplayColor(dept, index);
              const isDevelopment = isStatusServicoDepartment(dept);

              return (
                <button
                  key={`${dept.id ?? dept.nome}-${index}`}
                  type="button"
                  style={{
                    ...styles.moduleCard,
                    background: deptColor,
                    position: "relative",
                  }}
                  onClick={() => handleDepartmentClick(dept)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-3px)";
                    e.currentTarget.style.boxShadow =
                      "0 14px 30px rgba(0,0,0,0.22)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 8px 20px rgba(0,0,0,0.15)";
                  }}
                >
                  {isDevelopment && (
                    <span style={styles.developmentBadge}>DEV</span>
                  )}

                  <div style={styles.moduleIcon}>
                    <DeptInternetIcon
                      iconKey={dept.iconKey}
                      size={20}
                      color="#ffffff"
                      alt={dept.nome}
                    />
                  </div>

                  <div style={styles.moduleTitle}>
                    {dept.nome.split("\n").map((line, idx) => (
                      <div key={idx}>{line}</div>
                    ))}
                  </div>
                </button>
              );
            })}

            {departments.length === 0 && (
              <div style={styles.emptyState}>
                Nenhum departamento disponível para esta conta.
              </div>
            )}
          </div>

          {developmentModule && (
            <div style={styles.developmentPanel}>
              <div style={styles.developmentPanelHeader}>
                <div style={styles.developmentIconBox}>
                  <DeptInternetIcon
                    iconKey={developmentModule.iconKey || "lucide:activity"}
                    size={22}
                    color="#ffffff"
                    alt={developmentModule.nome}
                  />
                </div>

                <div style={styles.developmentTextGroup}>
                  <div style={styles.developmentEyebrow}>
                    Módulo indisponível
                  </div>

                  <h2 style={styles.developmentTitle}>
                    {developmentModule.nome} está em desenvolvimento
                  </h2>
                </div>
              </div>

              <p style={styles.developmentDescription}>
                Este módulo ainda não foi liberado para operação no sistema.
                Assim que a implantação for concluída, o acesso será
                disponibilizado normalmente por esta tela.
              </p>

              <div style={styles.developmentFooter}>
                <button
                  type="button"
                  style={styles.developmentCloseButton}
                  onClick={closeDevelopmentMessage}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.18)";
                    e.currentTarget.style.borderColor =
                      "rgba(255,255,255,0.28)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.10)";
                    e.currentTarget.style.borderColor =
                      "rgba(255,255,255,0.18)";
                  }}
                >
                  Entendido
                </button>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    position: "relative",
    width: "100%",
    minHeight: "100vh",
    overflow: "hidden",
    fontFamily:
      '"Nunito", "Inter", "Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    background: "#0d1117",
  },

  background: {
    position: "absolute",
    inset: 0,
    backgroundSize: "cover",
    backgroundPosition: "center center",
    backgroundRepeat: "no-repeat",
    transform: "none",
  },

  edgeBlur: {
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    backdropFilter: "blur(5px)",
    WebkitBackdropFilter: "blur(5px)",
    WebkitMaskImage:
      "radial-gradient(ellipse at center, transparent 0%, transparent 48%, rgba(0,0,0,0.16) 62%, rgba(0,0,0,0.70) 80%, black 100%)",
    maskImage:
      "radial-gradient(ellipse at center, transparent 0%, transparent 48%, rgba(0,0,0,0.16) 62%, rgba(0,0,0,0.70) 80%, black 100%)",
  },

  topTint: {
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    background:
      "linear-gradient(180deg, rgba(13,60,70,0.10) 0%, rgba(13,60,70,0.05) 12%, rgba(13,60,70,0.02) 24%, rgba(13,60,70,0) 36%)",
  },

  overlay: {
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(90deg, rgba(5,18,24,0.20) 0%, rgba(20,30,24,0.08) 35%, rgba(0,0,0,0.03) 100%)",
  },

  content: {
    position: "relative",
    zIndex: 2,
    minHeight: "100vh",
    padding: "20px 28px 28px 28px",
    boxSizing: "border-box",
  },

  customHeader: {
    height: 52,
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 10,
    padding: "0 2px",
  },

  customHeaderLeft: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },

  leftTopArea: {
    marginLeft: 10,
    marginTop: 40,
    width: "min(1000px, 100%)",
  },

  headerBlock: {
    marginBottom: 30,
  },

  title: {
    margin: 0,
    display: "flex",
    alignItems: "baseline",
    gap: 3,
    flexWrap: "wrap",
    lineHeight: 1.04,
    textShadow: "0 2px 12px rgba(0,0,0,0.22)",
  },

  titleHello: {
    fontSize: 20,
    fontWeight: 600,
    color: "#03e1f3",
    letterSpacing: 0,
  },

  titleName: {
    fontSize: 20,
    fontWeight: 600,
    color: "#FFFFFF",
    letterSpacing: 0,
  },

  subtitle: {
    margin: "6px 0 0 0",
    fontSize: 15,
    fontWeight: 500,
    lineHeight: 1.3,
    color: "rgba(255,255,255,0.9)",
    textShadow: "0 2px 14px rgba(0,0,0,0.20)",
  },

  modulesRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    alignItems: "stretch",
    marginBottom: 28,
  },

  moduleCard: {
    width: 122,
    minHeight: 118,
    border: "none",
    borderRadius: 12,
    padding: "14px 13px",
    color: "#fff",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "flex-start",
    cursor: "pointer",
    transition: "all 180ms ease",
    boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
    textAlign: "left",
    overflow: "hidden",
  },

  moduleIcon: {
    width: 24,
    height: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: 0.95,
  },

  moduleTitle: {
    fontSize: 14,
    lineHeight: 1.28,
    fontWeight: 800,
    whiteSpace: "pre-line",
    wordBreak: "break-word",
  },

  developmentBadge: {
    position: "absolute",
    top: 8,
    right: 8,
    height: 18,
    padding: "0 6px",
    borderRadius: 999,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(255,255,255,0.20)",
    border: "1px solid rgba(255,255,255,0.28)",
    color: "#ffffff",
    fontSize: 9,
    fontWeight: 900,
    letterSpacing: 0.8,
    lineHeight: 1,
  },

  developmentPanel: {
    width: "min(480px, calc(100vw - 56px))",
    borderRadius: 16,
    padding: 18,
    background:
      "linear-gradient(135deg, rgba(9,32,40,0.78) 0%, rgba(14,51,58,0.66) 100%)",
    border: "1px solid rgba(255,255,255,0.16)",
    boxShadow: "0 18px 42px rgba(0,0,0,0.24)",
    backdropFilter: "blur(14px)",
    WebkitBackdropFilter: "blur(14px)",
    color: "#ffffff",
  },

  developmentPanelHeader: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },

  developmentIconBox: {
    width: 42,
    height: 42,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(26,103,191,0.90)",
    boxShadow: "0 10px 22px rgba(0,0,0,0.22)",
    flexShrink: 0,
  },

  developmentTextGroup: {
    minWidth: 0,
  },

  developmentEyebrow: {
    marginBottom: 3,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.8,
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.62)",
  },

  developmentTitle: {
    margin: 0,
    fontSize: 18,
    fontWeight: 900,
    lineHeight: 1.18,
    color: "#ffffff",
  },

  developmentDescription: {
    margin: "14px 0 0 0",
    fontSize: 13.5,
    fontWeight: 500,
    lineHeight: 1.48,
    color: "rgba(255,255,255,0.82)",
  },

  developmentFooter: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: 16,
  },

  developmentCloseButton: {
    height: 34,
    padding: "0 14px",
    borderRadius: 10,
    border: "1px solid rgba(255,255,255,0.18)",
    background: "rgba(255,255,255,0.10)",
    color: "#ffffff",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    transition: "all 160ms ease",
  },

  emptyState: {
    minWidth: 240,
    padding: "14px 16px",
    borderRadius: 12,
    color: "rgba(255,255,255,0.92)",
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.14)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    fontSize: 13,
    fontWeight: 500,
  },
};

export default Home;
